import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { confirmAction, hideLoading, notifyError, showLoading } from '../../shared/notify.js';
import { passkeyRegistrieren, PASSKEY_UNTERSTUETZT } from '../../shared/webauthn.js';

// The recovery codes are shown exactly once, here, right after they are issued.
// Nothing stores them and no endpoint hands them out again - regenerating is
// the only way back to a readable set.
function codesAnzeigen(codes) {
  const dialog = document.getElementById('codes-dialog');
  if (!dialog) return;

  const liste = document.getElementById('codes-liste');
  liste.textContent = '';

  codes.forEach((code) => {
    const li = document.createElement('li');
    li.className = 'font-mono text-sm';
    li.textContent = code;
    liste.appendChild(li);
  });

  const download = document.getElementById('codes-download');
  if (download) {
    // A data: URL rather than a blob:, for the reason avatar.js gives for
    // FileReader - the Content-Security-Policy allows data: and not blob:.
    download.href = `data:text/plain;charset=utf-8,${encodeURIComponent(
      `Workly Intranet - Wiederherstellungscodes\n\n${codes.join('\n')}\n`
    )}`;
    download.setAttribute('download', 'workly-wiederherstellungscodes.txt');
  }

  dialog.showModal();
}

export function initPasskeys() {
  const card = document.getElementById('passkeys-card');
  if (!card) return;

  // The same card serves the settings page and the enrolment page the
  // requirement gate sends people to. Landing back on /settings from the latter
  // would walk straight into the gate again, so a freshly enrolled user is sent
  // to the dashboard instead.
  const ziel = window.location.pathname.startsWith('/passkey/') ? '/' : '/settings';

  const registrieren = document.getElementById('passkey-registrieren');
  const codesErneuern = document.getElementById('codes-erneuern');

  // Said once, up front, rather than after a failed ceremony: a browser without
  // WebAuthn or a page served over plain HTTP cannot register anything, and the
  // button would only ever produce an error.
  if (!PASSKEY_UNTERSTUETZT) {
    const hinweis = document.getElementById('passkey-nicht-unterstuetzt');
    if (hinweis) hinweis.classList.remove('hidden');
    if (registrieren) registrieren.disabled = true;
  }

  const fehler = (err, fallback) => {
    hideLoading();
    notifyError(err?.response?.data?.message || err?.message || fallback);
  };

  if (registrieren) {
    registrieren.addEventListener('click', async () => {
      registrieren.disabled = true;

      try {
        const { data } = await http.post('/users/me/passkeys/options');

        // No loading overlay around the ceremony itself: the browser puts its
        // own modal dialog up, and a spinner behind it only competes with it.
        const antwort = await passkeyRegistrieren(data.data.optionen);

        showLoading('Passkey wird gespeichert …');
        const gespeichert = await http.post('/users/me/passkeys', { antwort });
        hideLoading();

        const { codes } = gespeichert.data.data;
        if (codes && codes.length) {
          // The codes have to be read before the page reloads, so the reload
          // waits for the dialog to close rather than happening now.
          codesAnzeigen(codes);
          return;
        }

        window.location.href = withFlash(ziel, 'passkey-registriert');
      } catch (err) {
        registrieren.disabled = false;
        fehler(err, 'Der Passkey konnte nicht registriert werden.');
      }
    });
  }

  if (codesErneuern) {
    codesErneuern.addEventListener('click', async () => {
      const confirmed = await confirmAction({
        title: 'Wiederherstellungscodes erneuern',
        message:
          'Ihre bisherigen Codes werden dadurch ungültig. Möchten Sie einen neuen Satz erzeugen?',
        okText: 'Erneuern'
      });
      if (!confirmed) return;

      showLoading('Codes werden erzeugt …');

      try {
        const { data } = await http.post('/users/me/wiederherstellungscodes');
        hideLoading();
        codesAnzeigen(data.data.codes);
      } catch (err) {
        fehler(err, 'Die Codes konnten nicht erneuert werden.');
      }
    });
  }

  const codesSchliessen = document.getElementById('codes-schliessen');
  if (codesSchliessen) {
    codesSchliessen.addEventListener('click', () => {
      window.location.href = withFlash(ziel, 'codes-erneuert');
    });
  }

  card.addEventListener('click', async (event) => {
    const umbenennen = event.target.closest('[data-passkey-umbenennen]');
    const entfernen = event.target.closest('[data-passkey-entfernen]');

    if (umbenennen) {
      const id = umbenennen.dataset.passkeyUmbenennen;
      const aktuell = umbenennen.dataset.passkeyName || '';
      const name = window.prompt('Neuer Name für diesen Passkey:', aktuell);
      if (name === null) return;

      if (!name.trim()) {
        notifyError('Name ist erforderlich.');
        return;
      }

      showLoading('Passkey wird umbenannt …');
      try {
        await http.patch(`/users/me/passkeys/${id}`, { name });
        window.location.href = withFlash(ziel, 'passkey-umbenannt');
      } catch (err) {
        fehler(err, 'Der Passkey konnte nicht umbenannt werden.');
      }
      return;
    }

    if (entfernen) {
      const id = entfernen.dataset.passkeyEntfernen;
      const confirmed = await confirmAction({
        title: 'Passkey entfernen',
        message: 'Möchten Sie diesen Passkey wirklich entfernen?',
        okText: 'Entfernen'
      });
      if (!confirmed) return;

      showLoading('Passkey wird entfernt …');
      try {
        await http.delete(`/users/me/passkeys/${id}`);
        window.location.href = withFlash(ziel, 'passkey-entfernt');
      } catch (err) {
        fehler(err, 'Der Passkey konnte nicht entfernt werden.');
      }
    }
  });
}
